import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useArticlePagination from "../hooks/useArticlePagination";
import ArticleGrid from "../features/articles/ArticleGrid";
import SectionWrapper from "../ui/SectionWrapper";
import useCategoryNavigation from "../hooks/useCategoryNavigation";
import { CATEGORY_INFO } from "../../data/categories";
import articleService from "../../services/articleService";
import { getCategoryPath } from "../../utils/slugUtils";

// Helper function to format date to dd/mm/yyyy format
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString;
  }
};

// Helper function to transform API article to component format
const transformApiArticle = (apiArticle) => {
  return {
    id: apiArticle.id,
    slug: apiArticle.slug,
    title: apiArticle.title,
    excerpt: apiArticle.summary,
    author: apiArticle.author,
    date: formatDate(apiArticle.publish_date || apiArticle.created_at),
    readTime: apiArticle.read_time || 15,
    category: apiArticle.category,
    articleUrl: `https://www.tadreamk.com${getCategoryPath(apiArticle.category)}/${apiArticle.slug}`,
    image: apiArticle.cover_image_url,
    structuredContent: apiArticle.content_structure,
    translations: apiArticle.translations,
  };
};

// Category Header Component
const CategoryHeader = ({ categoryTitle, categoryAbstract, linkTo }) => {
  return (
    <div className="text-center mb-12">
      <Link
        to={linkTo}
        className="text-3xl md:text-4xl font-bold text-white mb-4 hover:text-violet-300 transition-colors duration-300 cursor-pointer block"
      >
        <h3>{categoryTitle}</h3>
      </Link>
      <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
        {categoryAbstract}
      </p>
    </div>
  );
};

// Category Section Component
const CategorySection = ({
  categoryTitle,
  categoryAbstract,
  articles,
  currentIndex,
  onPrevious,
  onNext,
  linkTo,
  className = "mb-20",
}) => {
  const { articlesPerPage, totalPages } = useArticlePagination(articles);

  // Override the pagination state with props for Magazine's multi-category setup
  const visibleArticles = (() => {
    const startIndex = currentIndex;
    const endIndex = Math.min(startIndex + articlesPerPage, articles.length);
    return articles.slice(startIndex, endIndex);
  })();

  const canGoPrev = currentIndex > 0;
  const canGoN = currentIndex + articlesPerPage < articles.length;

  return (
    <div className={className}>
      <CategoryHeader
        categoryTitle={categoryTitle}
        categoryAbstract={categoryAbstract}
        linkTo={linkTo}
      />

      <ArticleGrid
        articles={visibleArticles}
        currentIndex={currentIndex}
        articlesPerPage={articlesPerPage}
        totalPages={totalPages}
        totalArticles={articles.length}
        onPrevious={onPrevious}
        onNext={onNext}
        canGoPrevious={canGoPrev}
        canGoNext={canGoN}
      />
    </div>
  );
};

// Main Magazine Component
export default function Magazine() {
  const { t } = useTranslation("home");
  const [apiArticles, setApiArticles] = useState({
    "IP Law": [],
    "TadReamk AI": [],
    "TadReamk Design": [],
    "TadReamk-AIGC": [],
    "TadReamk-ERP": [],
  });

  // Fetch articles from API (backend only)
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const [lawResult, aiResult, designResult, aigcResult, erpResult] = await Promise.all([
          articleService.getArticles("IP Law"),
          articleService.getArticles("TadReamk AI"),
          articleService.getArticles("TadReamk Design"),
          articleService.getArticles("TadReamk-AIGC"),
          articleService.getArticles("TadReamk-ERP"),
        ]);

        setApiArticles({
          "IP Law": lawResult.success
            ? lawResult.data.map(transformApiArticle)
            : [],
          "TadReamk AI": aiResult.success
            ? aiResult.data.map(transformApiArticle)
            : [],
          "TadReamk Design": designResult.success
            ? designResult.data.map(transformApiArticle)
            : [],
          "TadReamk-AIGC": aigcResult.success
            ? aigcResult.data.map(transformApiArticle)
            : [],
          "TadReamk-ERP": erpResult.success
            ? erpResult.data.map(transformApiArticle)
            : [],
        });
      } catch (error) {
        console.error("Failed to fetch articles:", error);
      }
    };

    fetchArticles();
  }, []);

  // Use category navigation hook for all categories
  const { getCurrentIndex, getNavigationHandlers } =
    useCategoryNavigation(apiArticles);
  const navigationHandlers = getNavigationHandlers();

  return (
    <SectionWrapper>
      {/* IP Law Section */}
      <CategorySection
        categoryTitle={t("magazine.categories.ipLaw.title", {
          defaultValue: CATEGORY_INFO["IP Law"].title,
        })}
        categoryAbstract={t("magazine.categories.ipLaw.abstract", {
          defaultValue: CATEGORY_INFO["IP Law"].abstract,
        })}
        articles={apiArticles["IP Law"] || []}
        currentIndex={getCurrentIndex("IP Law")}
        onPrevious={navigationHandlers["IP Law"].handlePrevious}
        onNext={navigationHandlers["IP Law"].handleNext}
        linkTo="/magazine/law"
      />

      {/* TadReamk AI Section */}
      <CategorySection
        categoryTitle={t("magazine.categories.tadreamkAi.title", {
          defaultValue: CATEGORY_INFO["TadReamk AI"].title,
        })}
        categoryAbstract={t("magazine.categories.tadreamkAi.abstract", {
          defaultValue: CATEGORY_INFO["TadReamk AI"].abstract,
        })}
        articles={apiArticles["TadReamk AI"] || []}
        currentIndex={getCurrentIndex("TadReamk AI")}
        onPrevious={navigationHandlers["TadReamk AI"].handlePrevious}
        onNext={navigationHandlers["TadReamk AI"].handleNext}
        linkTo="/magazine/ai"
      />

      {/* TadReamk Design Section */}
      <CategorySection
        categoryTitle={t("magazine.categories.tadreamkDesign.title", {
          defaultValue: CATEGORY_INFO["TadReamk Design"].title,
        })}
        categoryAbstract={t("magazine.categories.tadreamkDesign.abstract", {
          defaultValue: CATEGORY_INFO["TadReamk Design"].abstract,
        })}
        articles={apiArticles["TadReamk Design"] || []}
        currentIndex={getCurrentIndex("TadReamk Design")}
        onPrevious={navigationHandlers["TadReamk Design"].handlePrevious}
        onNext={navigationHandlers["TadReamk Design"].handleNext}
        linkTo="/magazine/design"
      />

      {/* TadReamk-AIGC Section */}
      <CategorySection
        categoryTitle={t("magazine.categories.tadreamkAIGC.title", {
          defaultValue: CATEGORY_INFO["TadReamk-AIGC"].title,
        })}
        categoryAbstract={t("magazine.categories.tadreamkAIGC.abstract", {
          defaultValue: CATEGORY_INFO["TadReamk-AIGC"].abstract,
        })}
        articles={apiArticles["TadReamk-AIGC"] || []}
        currentIndex={getCurrentIndex("TadReamk-AIGC")}
        onPrevious={navigationHandlers["TadReamk-AIGC"].handlePrevious}
        onNext={navigationHandlers["TadReamk-AIGC"].handleNext}
        linkTo="/magazine/aigc"
      />

      {/* TadReamk-ERP Section - reduced bottom margin as last section */}
      <CategorySection
        categoryTitle={t("magazine.categories.tadreamkERP.title", {
          defaultValue: CATEGORY_INFO["TadReamk-ERP"].title,
        })}
        categoryAbstract={t("magazine.categories.tadreamkERP.abstract", {
          defaultValue: CATEGORY_INFO["TadReamk-ERP"].abstract,
        })}
        articles={apiArticles["TadReamk-ERP"] || []}
        currentIndex={getCurrentIndex("TadReamk-ERP")}
        onPrevious={navigationHandlers["TadReamk-ERP"].handlePrevious}
        onNext={navigationHandlers["TadReamk-ERP"].handleNext}
        linkTo="/magazine/erp"
        className="mb-8"
      />
    </SectionWrapper>
  );
}
